
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/ui/avatar";
import { UserProfile } from "@/lib/types";
import { Edit2, UserPlus, Settings, Star, Users, Shield } from "lucide-react";
import { FollowButton } from "./FollowButton";
import { ReportDialog } from "../ReportDialog";
import { Badge } from "../ui/badge";
import { InviteGenerator } from "@/components/invites/InviteGenerator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { SubscriptionDialog } from "@/components/subscription/SubscriptionDialog";
import { useSubscription } from "@/hooks/useSubscription";
import { Crown } from "lucide-react";

interface ProfileMainViewProps {
  profile: UserProfile;
  onEdit: () => void;
  onInviteFriends: () => void;
  onSettings: () => void;
  onFriends: () => void;
  onVerification: () => void;
}

export const ProfileMainView = ({ 
  profile, 
  onEdit, 
  onInviteFriends, 
  onSettings,
  onFriends,
  onVerification
}: ProfileMainViewProps) => {
  const karmaPoints = profile.stats?.karma?.total_points || 0;
  const { subscription, openCustomerPortal } = useSubscription();
  
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16">
            <img 
              src={profile.avatar_url || "https://github.com/shadcn.png"} 
              alt={profile.username} 
              className="rounded-full"
            />
          </Avatar>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold">{profile.username}</h2>
              <Badge variant="secondary" className="flex items-center gap-1">
                <Star className="h-3.5 w-3.5 text-yellow-500" />
                <span>{karmaPoints} karma</span>
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{profile.handle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={onEdit}>
            <Edit2 className="h-4 w-4" />
          </Button>
          <ReportDialog
            contentType="user"
            contentId={profile.id}
            reportedUserId={profile.id}
            onReport={(reason) => {
              console.log("User reported:", reason);
            }}
          />
        </div>
      </div>

      <p className="text-muted-foreground">{profile.bio}</p>

      <div className="flex flex-wrap gap-4">
        <FollowButton targetUserId={profile.id} />
        <Button variant="outline" onClick={onFriends}>
          <Users className="mr-2 h-4 w-4" />
          Friends
        </Button>
        <Button variant="outline" onClick={onVerification}>
          <Shield className="mr-2 h-4 w-4" />
          Get Verified
        </Button>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline">
              <UserPlus className="mr-2 h-4 w-4" />
              Invite Friends
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Zaproś znajomych</DialogTitle>
              <DialogDescription>
                Wygeneruj kody zaproszenia i udostępnij je znajomym
              </DialogDescription>
            </DialogHeader>
            <InviteGenerator />
          </DialogContent>
        </Dialog>
        {subscription.subscribed ? (
          <Button 
            variant="outline" 
            onClick={openCustomerPortal}
            className="border-yellow-500 text-yellow-700 hover:bg-yellow-50"
          >
            <Crown className="mr-2 h-4 w-4" />
            Zarządzaj Premium
          </Button>
        ) : (
          <SubscriptionDialog>
            <Button 
              variant="outline"
              className="border-purple-500 text-purple-700 hover:bg-purple-50"
            >
              <Crown className="mr-2 h-4 w-4" />
              Buy Premium
            </Button>
          </SubscriptionDialog>
        )}
        <Button variant="outline" onClick={onSettings}>
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </Button>
      </div>
    </div>
  );
};
